import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { 
  Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction,
  Divider, Box, Select, MenuItem, InputLabel, FormControl, Card, CardContent,
  Chip, Grid, Switch, FormControlLabel, Accordion, AccordionSummary, AccordionDetails,
  Avatar, Tooltip, InputAdornment, useTheme, CircularProgress
} from "@material-ui/core";
import {
  Add, Edit, Delete, FileCopy, Save, Close, ExpandMore, AttachFile, Schedule,
  CloudUpload, InsertLink, PlayArrow, ViewList, ViewModule, Search as SearchIcon,
  Announcement, Build, Lock,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import useQueues from "../../hooks/useQueues";
import officialCampaignService from "../../services/officialCampaignService";
import MessageVariablesPicker from "../../components/MessageVariablesPicker";
import TemplatePreview from "../../components/WhatsAppTemplateEditor/TemplatePreview";
import { templateToFormState } from "../../components/WhatsAppTemplateEditor/templatePayload";
import {
  getOfficialTemplateCategoryMeta,
  extractOfficialTemplateVariables,
  countOfficialTemplateVariables,
  hasEmptyOfficialTemplateValues,
  buildOfficialSendComponents,
  hydrateOfficialVarValuesFromSendComponents,
  applyOfficialPreviewSubstitution,
  emptyOficialVarDescriptors,
} from "../../utils/officialTemplateHelpers";

const DEFAULT_DISPATCH_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];

const categoryMetaWithIcon = (category) => {
  const meta = getOfficialTemplateCategoryMeta(category);
  const Icon =
    meta.key === "MARKETING"
      ? Announcement
      : meta.key === "AUTHENTICATION"
        ? Lock
        : Build;
  return { ...meta, Icon };
};

const emptyStep = (order = 1) => ({
  order,
  message: "",
  timeUnit: "minutes",
  timeValue: 0,
  mediaUrl: "",
  mediaType: "url",
  oficialTemplateName: "",
  oficialTemplateLanguage: "",
  oficialTemplateComponentsJson: "[]",
});

const emptyForm = () => ({
  id: null,
  name: "",
  description: "",
  templateType: "personalizado",
  condition: "queue",
  conditionValue: "",
  steps: [],
  stopConditions: [],
  pauseConditions: [],
  whatsappId: null,
  dispatchDays: [...DEFAULT_DISPATCH_DAYS],
  dispatchStartTime: "09:00",
  dispatchEndTime: "18:00",
  pauseOutsideHours: false,
});

const clearStepOficialTemplate = (step) => ({
  ...step,
  oficialTemplateName: "",
  oficialTemplateLanguage: "",
  oficialTemplateComponentsJson: "[]",
});

const parseDispatchDays = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [...DEFAULT_DISPATCH_DAYS];
    } catch {
      return [...DEFAULT_DISPATCH_DAYS];
    }
  }
  return [...DEFAULT_DISPATCH_DAYS];
};

const FloupPage = () => {
  const theme = useTheme();
  const { findAll: findAllQueues } = useQueues();
  const [floups, setFloups] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState(emptyForm());
  const [preview, setPreview] = useState("");
  const [tags, setTags] = useState([]);
  const [queues, setQueues] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [floupToDelete, setFloupToDelete] = useState(null);
  const [stepTemplateModalIdx, setStepTemplateModalIdx] = useState(null);
  const [officialTemplates, setOfficialTemplates] = useState([]);
  const [loadingOfficialTemplates, setLoadingOfficialTemplates] = useState(false);
  const [stepOficialVars, setStepOficialVars] = useState({});
  const [searchParam, setSearchParam] = useState("");
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("floup_viewMode");
      return saved === "cards" || saved === "list" ? saved : "list";
    } catch {
      return "list";
    }
  });
  const activeTplVarRef = useRef(null);

  const handleChangeViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem("floup_viewMode", mode);
    } catch {
      /* ignore */
    }
  };

  const filteredFloups = useMemo(() => {
    const q = searchParam.trim().toLowerCase();
    if (!q) return floups;
    return floups.filter(
      (f) =>
        (f.name || "").toLowerCase().includes(q) ||
        (f.description || "").toLowerCase().includes(q)
    );
  }, [floups, searchParam]);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('typeArch', 'floup');
    if (form.id) {
      formData.append('floupId', form.id.toString());
    }
    try {
      const response = await api.post('/plugins/floup/upload', formData, { 
        headers: { 
          'Content-Type': 'multipart/form-data' 
        },
      });
      return (response.data && response.data.url) ? response.data.url : null;
    } catch (error) {
      console.error('[FLOUP] uploadFile → Erro:', error.response?.data || error.message);
      return null;
    }
  };

  const deleteFile = async (fileUrl) => {
    if (!fileUrl) return;
    try {
      await api.delete('/plugins/floup/upload', { data: { fileUrl } });
      return true;
    } catch (error) {
      console.error('[FLOUP] deleteFile → Erro:', error.response?.data || error.message);
      return false;
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/plugins/floup");
      setFloups(data);
    } catch (err) {
      console.error('[FLOUP] Erro ao carregar floups:', err);
    }
    
    try {
      const { data: tagList } = await api.get('/tags/list');
      setTags(tagList || []);
    } catch (err) {
      console.error('[FLOUP] Erro ao carregar tags:', err);
    }
    
    try {
      const queueList = await findAllQueues();
      setQueues(queueList || []);
    } catch (err) {
      console.error('[FLOUP] Erro ao carregar filas:', err);
    }

    try {
      const { data } = await api.get("/whatsapp/filter", {
        params: { session: 0, forSchedule: "true" },
      });
      setWhatsapps(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[FLOUP] Erro ao carregar conexões:", err);
      setWhatsapps([]);
    }
  };

  useEffect(() => { load(); }, [findAllQueues]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedSendWhatsapp = useMemo(
    () => whatsapps.find((w) => w.id === form.whatsappId) || null,
    [whatsapps, form.whatsappId]
  );

  const isSelectedOfficial = selectedSendWhatsapp?.channel === "whatsapp_oficial";

  const loadOfficialTemplates = async (whatsappId) => {
    if (!whatsappId) {
      setOfficialTemplates([]);
      return;
    }
    setLoadingOfficialTemplates(true);
    try {
      const { data } = await officialCampaignService.templates(whatsappId);
      setOfficialTemplates(data?.data || []);
    } catch (err) {
      console.error("[FLOUP] Erro ao carregar templates oficiais:", err);
      setOfficialTemplates([]);
    } finally {
      setLoadingOfficialTemplates(false);
    }
  };

  const openStepTemplateModal = (idx) => {
    setStepTemplateModalIdx(idx);
    if (form.whatsappId) {
      loadOfficialTemplates(form.whatsappId);
    }
  };

  const closeStepTemplateModal = () => {
    setStepTemplateModalIdx(null);
  };

  const syncStepOficialVars = useCallback((stepIdx, tpl, existingSendJson) => {
    const components = tpl?.components || [];
    const descriptors = extractOfficialTemplateVariables(components);
    let values = {};
    if (existingSendJson) {
      try {
        const parsed = JSON.parse(existingSendJson);
        if (
          Array.isArray(parsed) &&
          parsed.some((c) => Array.isArray(c?.parameters) && c.parameters.length)
        ) {
          values = hydrateOfficialVarValuesFromSendComponents(parsed, descriptors);
        }
      } catch (_e) {
        values = {};
      }
    }
    const sendComps =
      countOfficialTemplateVariables(descriptors) > 0
        ? buildOfficialSendComponents(components, values, descriptors)
        : [];
    setStepOficialVars((prev) => ({
      ...prev,
      [stepIdx]: {
        descriptors,
        values,
        listingComponents: components,
        category: tpl?.category || "UTILITY",
      },
    }));
    return JSON.stringify(sendComps);
  }, []);

  const updateStepOficialVarValue = (stepIdx, componentType, index, value) => {
    setStepOficialVars((prev) => {
      const current = prev[stepIdx] || {
        descriptors: emptyOficialVarDescriptors(),
        values: {},
        listingComponents: [],
      };
      const nextValues = {
        ...current.values,
        [componentType]: {
          ...(current.values?.[componentType] || {}),
          [index]: { value },
        },
      };
      const descriptors =
        countOfficialTemplateVariables(current.descriptors) > 0
          ? current.descriptors
          : extractOfficialTemplateVariables(current.listingComponents);
      const sendComps = buildOfficialSendComponents(
        current.listingComponents,
        nextValues,
        descriptors
      );
      setForm((prevForm) => {
        const steps = [...prevForm.steps];
        if (!steps[stepIdx]) return prevForm;
        steps[stepIdx] = {
          ...steps[stepIdx],
          oficialTemplateComponentsJson: JSON.stringify(sendComps),
        };
        return { ...prevForm, steps };
      });
      return {
        ...prev,
        [stepIdx]: { ...current, values: nextValues, descriptors },
      };
    });
  };

  const insertMustacheIntoStepTplVar = (stepIdx, msgVar) => {
    const target = activeTplVarRef.current;
    if (!target || target.stepIdx !== stepIdx) {
      toast.info(
        "Clique em um campo de variável do template e depois escolha a variável do sistema."
      );
      return;
    }
    const { componentType, index } = target;
    const current =
      stepOficialVars?.[stepIdx]?.values?.[componentType]?.[index]?.value || "";
    updateStepOficialVarValue(stepIdx, componentType, index, `${current}${msgVar}`);
  };

  useEffect(() => {
    if (open && isSelectedOfficial && form.whatsappId) {
      loadOfficialTemplates(form.whatsappId);
    }
  }, [open, isSelectedOfficial, form.whatsappId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !isSelectedOfficial || !officialTemplates.length) return;
    const updates = {};
    const varsUpdates = {};
    form.steps.forEach((step, idx) => {
      if (!step?.oficialTemplateName) return;
      if (stepOficialVars[idx]?.listingComponents?.length) return;
      const tpl = officialTemplates.find(
        (t) =>
          t.name === step.oficialTemplateName &&
          String(t.language || "") === String(step.oficialTemplateLanguage || "")
      );
      if (!tpl) return;
      const components = tpl?.components || [];
      const descriptors = extractOfficialTemplateVariables(components);
      let values = {};
      try {
        const parsed = JSON.parse(step.oficialTemplateComponentsJson || "[]");
        if (
          Array.isArray(parsed) &&
          parsed.some((c) => Array.isArray(c?.parameters) && c.parameters.length)
        ) {
          values = hydrateOfficialVarValuesFromSendComponents(parsed, descriptors);
        }
      } catch (_e) {
        values = {};
      }
      const sendComps =
        countOfficialTemplateVariables(descriptors) > 0
          ? buildOfficialSendComponents(components, values, descriptors)
          : [];
      varsUpdates[idx] = {
        descriptors,
        values,
        listingComponents: components,
        category: tpl?.category || "UTILITY",
      };
      updates[idx] = JSON.stringify(sendComps);
    });
    if (Object.keys(varsUpdates).length) {
      setStepOficialVars((prev) => ({ ...prev, ...varsUpdates }));
    }
    if (Object.keys(updates).length) {
      setForm((prev) => {
        let changed = false;
        const steps = prev.steps.map((step, idx) => {
          if (updates[idx] == null || updates[idx] === step.oficialTemplateComponentsJson) {
            return step;
          }
          changed = true;
          return { ...step, oficialTemplateComponentsJson: updates[idx] };
        });
        return changed ? { ...prev, steps } : prev;
      });
    }
  }, [open, isSelectedOfficial, officialTemplates]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (isSelectedOfficial) {
      for (let i = 0; i < form.steps.length; i++) {
        const step = form.steps[i];
        if (!step?.oficialTemplateName) continue;
        const varsState = stepOficialVars[i];
        let descriptors = varsState?.descriptors;
        let values = varsState?.values || {};
        if (!descriptors || countOfficialTemplateVariables(descriptors) === 0) {
          const tpl = officialTemplates.find(
            (t) =>
              t.name === step.oficialTemplateName &&
              String(t.language || "") === String(step.oficialTemplateLanguage || "")
          );
          descriptors = extractOfficialTemplateVariables(tpl?.components || []);
          if (!Object.keys(values).length) {
            try {
              values = hydrateOfficialVarValuesFromSendComponents(
                JSON.parse(step.oficialTemplateComponentsJson || "[]"),
                descriptors
              );
            } catch (_e) {
              values = {};
            }
          }
        }
        if (
          countOfficialTemplateVariables(descriptors) > 0 &&
          hasEmptyOfficialTemplateValues(descriptors, values)
        ) {
          toast.error(
            `Preencha todas as variáveis do template Meta no passo ${step.order || i + 1}.`
          );
          return;
        }
      }
    }

    // Garantir que todos os campos dos steps estão presentes, preservando TODOS os campos originais
    const stepsWithDefaults = form.steps.map((step, index) => ({
      order: step.order || (index + 1),
      message: step.message || '',
      timeUnit: step.timeUnit || 'minutes',
      timeValue: step.timeValue || 0,
      mediaUrl: step.mediaUrl || '',
      mediaType: step.mediaType || 'url',
      oficialTemplateName: step.oficialTemplateName || null,
      oficialTemplateLanguage: step.oficialTemplateLanguage || null,
      oficialTemplateComponentsJson: step.oficialTemplateComponentsJson || "[]",
    }));
    
    const payload = { 
      name: form.name, 
      description: form.description, 
      templateType: form.templateType, 
      condition: form.condition || "queue",
      conditionValue: form.conditionValue || "",
      isActive: true, 
      steps: stepsWithDefaults, 
      stopConditions: form.stopConditions || [], 
      pauseConditions: form.pauseConditions || [],
      whatsappId: form.whatsappId || null,
      oficialTemplateName: null,
      oficialTemplateLanguage: null,
      oficialTemplateComponentsJson: null,
      dispatchDays: Array.isArray(form.dispatchDays)
        ? JSON.stringify(form.dispatchDays)
        : form.dispatchDays,
      dispatchStartTime: form.dispatchStartTime || "09:00",
      dispatchEndTime: form.dispatchEndTime || "18:00",
      pauseOutsideHours: !!form.pauseOutsideHours,
    };
    
    try {
      if (form.id) {
        await api.put(`/plugins/floup/${form.id}`, payload);
      } else {
        await api.post(`/plugins/floup`, payload);
      }
      
      setOpen(false);
      resetForm();
      await load();
    } catch (error) {
      console.error('[FLOUP] handleSave → Erro ao salvar:', error);
      alert('Erro ao salvar o Follow UP. Verifique o console para mais detalhes.');
    }
  };

  const resetForm = () => {
    setForm(emptyForm());
    setCurrentStep(0);
    setPreview("");
    setStepTemplateModalIdx(null);
    setOfficialTemplates([]);
    setStepOficialVars({});
    activeTplVarRef.current = null;
  };

  const handleDelete = (id) => {
    const floup = floups.find(f => f.id === id);
    setFloupToDelete({ id, name: floup?.name || 'este floup' });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (floupToDelete) {
      try {
        await api.delete(`/plugins/floup/${floupToDelete.id}`);
        await load();
        setDeleteConfirmOpen(false);
        setFloupToDelete(null);
      } catch (error) {
        console.error('[FLOUP] Erro ao deletar floup:', error);
        alert('Erro ao deletar o Follow UP. Verifique o console para mais detalhes.');
      }
    }
  };
  const handleDuplicate = async (id) => { await api.post(`/plugins/floup/${id}/duplicate`); await load(); };

  const updateStep = (idx, key, value) => {
    setForm(prevForm => {
      const steps = [...prevForm.steps];
      if (!steps[idx]) {
        console.warn(`[FLOUP] updateStep → Step ${idx} não existe, criando novo step`);
        steps[idx] = emptyStep(idx + 1);
      }
      const updatedStep = { ...steps[idx], [key]: value };
      steps[idx] = updatedStep;
      const newForm = { ...prevForm, steps };
      if (key === "message") setPreview(value);
      return newForm;
    });
  };

  const addStep = () => {
    const newStep = emptyStep(form.steps.length + 1);
    setForm({ ...form, steps: [...form.steps, newStep] });
  };
  const removeStep = (idx) => {
    setForm({
      ...form,
      steps: form.steps
        .filter((_, i) => i !== idx)
        .map((s, i) => ({ ...s, order: i + 1 })),
    });
    setStepOficialVars((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const ki = Number(k);
        if (ki < idx) next[ki] = prev[ki];
        else if (ki > idx) next[ki - 1] = prev[ki];
      });
      return next;
    });
    if (stepTemplateModalIdx === idx) setStepTemplateModalIdx(null);
    else if (stepTemplateModalIdx != null && stepTemplateModalIdx > idx) {
      setStepTemplateModalIdx(stepTemplateModalIdx - 1);
    }
  };

  const getTemplateTypeConfig = (type) => {
    switch (type) {
      case "cliente":
        return {
          title: "Template para Clientes",
          description: "Fluxo automatizado para clientes existentes",
          defaultSteps: [
            {
              ...emptyStep(1),
              message: "Olá! Como podemos ajudá-lo hoje?",
            },
          ],
        };
      case "lead":
        return {
          title: "Template para Leads",
          description: "Fluxo de nutrição para novos leads",
          defaultSteps: [
            {
              ...emptyStep(1),
              message: "Olá! Bem-vindo! Temos uma oferta especial para você.",
              timeUnit: "hours",
              timeValue: 2,
            },
          ],
        };
      case "personalizado":
      default:
        return { title: "Template Personalizado", description: "Crie seu próprio fluxo com condições específicas", defaultSteps: [] };
    }
  };

  const isImageFile = (url) => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    
    // Verificar se é uma URL base64 (data:image)
    if (lowerUrl.startsWith('data:image/')) {
      return true;
    }
    
    // Verificar extensões de imagem
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    if (imageExtensions.some(ext => lowerUrl.includes(ext))) {
      return true;
    }
    
    // Verificar se contém 'image' na URL (para APIs que retornam imagens)
    if (lowerUrl.includes('image') && (lowerUrl.includes('http') || lowerUrl.includes('https'))) {
      return true;
    }
    
    return false;
  };

  const isVideoFile = (url) => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    
    // Verificar se é uma URL base64 (data:video)
    if (lowerUrl.startsWith('data:video/')) {
      return true;
    }
    
    // Verificar extensões de vídeo
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.mkv', '.flv', '.wmv'];
    if (videoExtensions.some(ext => lowerUrl.includes(ext))) {
      return true;
    }
    
    // Verificar se contém 'video' na URL
    if (lowerUrl.includes('video') && (lowerUrl.includes('http') || lowerUrl.includes('https'))) {
      return true;
    }
    
    return false;
  };

  const formatStepTimeUnitLabel = (unit, value) => {
    const n = Number(value) || 0;
    const labels = {
      minutes: n === 1 ? "minuto" : "minutos",
      hours: n === 1 ? "hora" : "horas",
      days: n === 1 ? "dia" : "dias",
      weeks: n === 1 ? "semana" : "semanas",
      months: n === 1 ? "mês" : "meses",
      years: n === 1 ? "ano" : "anos",
    };
    return labels[unit] || unit || "minutos";
  };

  const renderStepForm = (step, idx) => (
    <Accordion key={idx} expanded={currentStep === idx} onChange={() => setCurrentStep(currentStep === idx ? -1 : idx)}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box display="flex" alignItems="center" width="100%">
          <Typography variant="h6" style={{ marginRight: 16, color: theme.palette.text.primary }}>Passo {step.order}</Typography>
          <Chip label={step.message ? step.message.substring(0, 30) + "..." : "Mensagem vazia"} size="small" color={step.message ? "primary" : "default"} />
          {step.mediaUrl && (<Chip icon={<AttachFile />} label="Anexo" size="small" color="secondary" style={{ marginLeft: 8 }} />)}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box width="100%">
          <Paper 
            elevation={0}
            style={{ 
              padding: 16, 
              backgroundColor: theme.palette.type === 'dark' 
                ? theme.palette.background.default 
                : '#f5f5f5',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 8
            }}
          >
            <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Mensagem" value={step.message} onChange={e => updateStep(idx, "message", e.target.value)} multiline rows={3} inputProps={{ maxLength: 500 }} helperText={`${(step.message || "").length}/500 caracteres`} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Valor" value={step.timeValue || 0} onChange={e => updateStep(idx, "timeValue", parseInt(e.target.value || "0"))} inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel 
                  shrink 
                  style={{ backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f5f5f5', padding: '0 4px' }}
                >
                  Unidade de Tempo
                </InputLabel>
                <Select 
                  value={step.timeUnit || "minutes"} 
                  onChange={e => updateStep(idx, "timeUnit", e.target.value)}
                  notched
                >
                  <MenuItem value="minutes">Minutos</MenuItem>
                  <MenuItem value="hours">Horas</MenuItem>
                  <MenuItem value="days">Dias</MenuItem>
                  <MenuItem value="weeks">Semanas</MenuItem>
                  <MenuItem value="months">Meses</MenuItem>
                  <MenuItem value="years">Anos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" style={{ paddingTop: 8, color: theme.palette.text.secondary, lineHeight: 1.4 }}>
                {idx === 0
                  ? `Enviar após ${step.timeValue || 0} ${formatStepTimeUnitLabel(step.timeUnit || "minutes", step.timeValue)} a partir da ativação do Follow-up`
                  : `Enviar após ${step.timeValue || 0} ${formatStepTimeUnitLabel(step.timeUnit || "minutes", step.timeValue)} a partir do envio do passo ${idx}`}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom style={{ color: theme.palette.text.primary, marginBottom: 8 }}>Anexo/Link</Typography>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <FormControl style={{ minWidth: 100 }}>
                  <Select 
                    value={step.mediaType || "url"} 
                    onChange={e => updateStep(idx, "mediaType", e.target.value)}
                    style={{ color: theme.palette.text.primary }}
                    variant="outlined"
                    size="small"
                  >
                    <MenuItem value="url">Link</MenuItem>
                    <MenuItem value="upload">Upload</MenuItem>
                  </Select>
                </FormControl>
                {step.mediaType === "url" ? (
                  <TextField 
                    size="small"
                    placeholder="Cole a URL aqui" 
                    value={step.mediaUrl || ""} 
                    onChange={e => updateStep(idx, "mediaUrl", e.target.value)} 
                    InputProps={{ 
                      startAdornment: <InputAdornment position="start"><InsertLink style={{ fontSize: 16 }} /></InputAdornment>,
                      endAdornment: step.mediaUrl ? (
                        <InputAdornment position="end">
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {isImageFile(step.mediaUrl) && (
                              <img 
                                src={step.mediaUrl} 
                                alt="Preview" 
                                style={{ 
                                  width: 24, 
                                  height: 24,
                                  objectFit: 'cover', 
                                  borderRadius: 2,
                                  border: `1px solid ${theme.palette.divider}`
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            {isVideoFile(step.mediaUrl) && (
                              <Box
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 2,
                                  border: `1px solid ${theme.palette.divider}`,
                                  overflow: 'hidden',
                                  position: 'relative',
                                  cursor: 'pointer',
                                  flexShrink: 0
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const videoUrl = step.mediaUrl;
                                  const newWindow = window.open('', '_blank');
                                  if (newWindow) {
                                    newWindow.document.write(`
                                      <html>
                                        <head><title>Player de Vídeo</title></head>
                                        <body style="margin:0;padding:0;background:#000;display:flex;justify-content:center;align-items:center;height:100vh;">
                                          <video controls autoplay style="max-width:100%;max-height:100%;">
                                            <source src="${videoUrl}" type="video/mp4">
                                            Seu navegador não suporta o elemento de vídeo.
                                          </video>
                                        </body>
                                      </html>
                                    `);
                                  }
                                }}
                              >
                                <video
                                  src={step.mediaUrl}
                                  preload="metadata"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                                <Box
                                  style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    borderRadius: '50%',
                                    padding: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <PlayArrow style={{ fontSize: 12, color: '#fff' }} />
                                </Box>
                              </Box>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => {
                                updateStep(idx, "mediaUrl", "");
                                updateStep(idx, "uploadedFile", null);
                              }}
                              title="Remover link"
                              style={{ padding: 2 }}
                            >
                              <Delete style={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </InputAdornment>
                      ) : null
                    }}
                    style={{ flex: 1, minWidth: 150, maxWidth: 400 }}
                  />
                ) : (
                  <>
                    <input accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt" style={{ display: 'none' }} id={`file-upload-${idx}`} type="file" onChange={async (e) => { 
                      const inputElement = e.target;
                      const file = e.target.files[0]; 
                      if (file) { 
                        updateStep(idx, "mediaUrl", `Enviando ${file.name}...`); 
                        const uploadedUrl = await uploadFile(file); 
                        if (uploadedUrl) { 
                          setForm(prevForm => {
                            const steps = [...prevForm.steps];
                            if (!steps[idx]) {
                              console.warn(`[FLOUP] onChange → Step ${idx} não existe, criando novo step`);
                              steps[idx] = emptyStep(idx + 1);
                            }
                            steps[idx] = { ...steps[idx], mediaUrl: uploadedUrl, uploadedFile: file };
                            return { ...prevForm, steps };
                          });
                        } else { 
                          console.error('[FLOUP] onChange → Erro no upload do arquivo - uploadedUrl é null');
                          updateStep(idx, "mediaUrl", ""); 
                          alert("Erro no upload do arquivo. Verifique o console para mais detalhes."); 
                        } 
                        if (inputElement) {
                          inputElement.value = '';
                        }
                      } else {
                        if (inputElement) {
                          inputElement.value = '';
                        }
                      }
                    }} />
                    {!step.mediaUrl || step.mediaUrl.includes('Enviando') ? (
                      <>
                        <label htmlFor={`file-upload-${idx}`}>
                          <Button 
                            variant="outlined" 
                            component="span" 
                            startIcon={<CloudUpload />}
                            size="small"
                          >
                            Escolher Arquivo
                          </Button>
                        </label>
                        {step.mediaUrl && step.mediaUrl.includes('Enviando') && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <CloudUpload style={{ fontSize: 16, color: theme.palette.primary.main }} />
                            <Typography variant="caption" style={{ color: theme.palette.text.secondary }}>
                              {step.mediaUrl.replace('Enviando ', '')}
                            </Typography>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box 
                        display="flex" 
                        alignItems="center" 
                        gap={0.5}
                        style={{ 
                          flex: 1,
                          padding: '4px 8px',
                          borderRadius: 4,
                          backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f5f5f5',
                          border: `1px solid ${theme.palette.divider}`,
                          minWidth: 0,
                          maxWidth: 400
                        }}
                      >
                        {isImageFile(step.mediaUrl) ? (
                          <img 
                            src={step.mediaUrl} 
                            alt="Preview" 
                            style={{ 
                              width: 24, 
                              height: 24,
                              objectFit: 'cover', 
                              borderRadius: 2,
                              border: `1px solid ${theme.palette.divider}`,
                              flexShrink: 0
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const icon = e.target.nextElementSibling;
                              if (icon) icon.style.display = 'flex';
                            }}
                          />
                        ) : isVideoFile(step.mediaUrl) ? (
                          <Box
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: 2,
                              border: `1px solid ${theme.palette.divider}`,
                              overflow: 'hidden',
                              position: 'relative',
                              cursor: 'pointer',
                              flexShrink: 0
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const videoUrl = step.mediaUrl;
                              const newWindow = window.open('', '_blank');
                              if (newWindow) {
                                newWindow.document.write(`
                                  <html>
                                    <head><title>Player de Vídeo</title></head>
                                    <body style="margin:0;padding:0;background:#000;display:flex;justify-content:center;align-items:center;height:100vh;">
                                      <video controls autoplay style="max-width:100%;max-height:100%;">
                                        <source src="${videoUrl}" type="video/mp4">
                                        Seu navegador não suporta o elemento de vídeo.
                                      </video>
                                    </body>
                                  </html>
                                `);
                              }
                            }}
                          >
                            <video
                              src={step.mediaUrl}
                              preload="metadata"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const icon = e.target.nextElementSibling;
                                if (icon) icon.style.display = 'flex';
                              }}
                            />
                            <Box
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                borderRadius: '50%',
                                padding: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <PlayArrow style={{ fontSize: 12, color: '#fff' }} />
                            </Box>
                          </Box>
                        ) : null}
                        <Box 
                          display={isImageFile(step.mediaUrl) || isVideoFile(step.mediaUrl) ? 'none' : 'flex'}
                          alignItems="center"
                          style={{ color: theme.palette.text.secondary, flexShrink: 0 }}
                        >
                          <AttachFile style={{ fontSize: 16 }} />
                        </Box>
                        <Typography 
                          variant="caption" 
                          style={{ 
                            color: theme.palette.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          {step.mediaUrl.split('/').pop() || step.mediaUrl}
                        </Typography>
                        <IconButton 
                          size="small"
                          onClick={async () => {
                            const fileUrlToDelete = step.mediaUrl;
                            await deleteFile(fileUrlToDelete);
                            updateStep(idx, "mediaUrl", "");
                            updateStep(idx, "uploadedFile", null);
                          }}
                          title="Remover mídia"
                          style={{ padding: 2, marginLeft: 4 }}
                        >
                          <Delete style={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              {isSelectedOfficial && (
                <Box
                  style={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                    backgroundColor:
                      theme.palette.type === "dark"
                        ? theme.palette.background.paper
                        : "#fafafa",
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Template Meta (janela fechada)
                  </Typography>
                  {step.oficialTemplateName ? (
                    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8, marginBottom: 8 }}>
                      <Chip
                        label={`${step.oficialTemplateName} — ${step.oficialTemplateLanguage || "—"}`}
                        color="primary"
                        size="small"
                      />
                      {(() => {
                        const varsState = stepOficialVars[idx];
                        const tpl = officialTemplates.find(
                          (t) =>
                            t.name === step.oficialTemplateName &&
                            String(t.language || "") ===
                              String(step.oficialTemplateLanguage || "")
                        );
                        const category =
                          varsState?.category || tpl?.category || "UTILITY";
                        const meta = categoryMetaWithIcon(category);
                        const Icon = meta.Icon;
                        return (
                          <Chip
                            size="small"
                            icon={<Icon style={{ color: meta.color }} />}
                            label={meta.label}
                            style={{
                              backgroundColor: meta.backgroundColor,
                              color: meta.color,
                            }}
                          />
                        );
                      })()}
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginBottom: 8 }}
                    >
                      Nenhum template selecionado
                    </Typography>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => openStepTemplateModal(idx)}
                    style={{ marginBottom: 8 }}
                  >
                    Escolher template Meta
                  </Button>
                  {step.oficialTemplateName &&
                    (() => {
                      const varsState = stepOficialVars[idx];
                      const tpl = officialTemplates.find(
                        (t) =>
                          t.name === step.oficialTemplateName &&
                          String(t.language || "") ===
                            String(step.oficialTemplateLanguage || "")
                      );
                      const listingComponents =
                        varsState?.listingComponents?.length
                          ? varsState.listingComponents
                          : Array.isArray(tpl?.components)
                            ? tpl.components
                            : [];
                      const descriptors =
                        countOfficialTemplateVariables(varsState?.descriptors) > 0
                          ? varsState.descriptors
                          : extractOfficialTemplateVariables(listingComponents);
                      const values = varsState?.values || {};
                      const hasVars =
                        countOfficialTemplateVariables(descriptors) > 0;

                      if (!listingComponents.length && !hasVars) return null;

                      const previewTpl = {
                        name: step.oficialTemplateName,
                        language: step.oficialTemplateLanguage,
                        category:
                          varsState?.category || tpl?.category || "UTILITY",
                        components: listingComponents.map((c) => {
                          const type = String(c.type || "").toLowerCase();
                          if (type === "body") {
                            return {
                              ...c,
                              text: applyOfficialPreviewSubstitution(
                                c.text,
                                descriptors.body,
                                values.body
                              ),
                            };
                          }
                          if (type === "header") {
                            return {
                              ...c,
                              text: applyOfficialPreviewSubstitution(
                                c.text,
                                descriptors.header,
                                values.header
                              ),
                            };
                          }
                          return c;
                        }),
                      };
                      const previewForm = templateToFormState(previewTpl);

                      return (
                        <Box style={{ marginTop: 8 }}>
                          {hasVars && (
                            <Box style={{ marginBottom: 8 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Variáveis do template
                              </Typography>
                              {Object.keys(descriptors).map((componentType) =>
                                (descriptors[componentType] || []).map(
                                  (variable, varIndex) => (
                                    <TextField
                                      key={`${componentType}-${varIndex}`}
                                      fullWidth
                                      margin="dense"
                                      variant="outlined"
                                      required
                                      label={variable.prompt}
                                      value={
                                        values?.[componentType]?.[varIndex]
                                          ?.value || ""
                                      }
                                      onFocus={() => {
                                        activeTplVarRef.current = {
                                          stepIdx: idx,
                                          componentType,
                                          index: varIndex,
                                        };
                                      }}
                                      onChange={(e) =>
                                        updateStepOficialVarValue(
                                          idx,
                                          componentType,
                                          varIndex,
                                          e.target.value
                                        )
                                      }
                                      error={
                                        !String(
                                          values?.[componentType]?.[varIndex]
                                            ?.value || ""
                                        ).trim()
                                      }
                                      helperText={
                                        !String(
                                          values?.[componentType]?.[varIndex]
                                            ?.value || ""
                                        ).trim()
                                          ? "Obrigatório para o envio"
                                          : " "
                                      }
                                    />
                                  )
                                )
                              )}
                              <Box style={{ marginTop: 4 }}>
                                <MessageVariablesPicker
                                  showSchedulingVars={true}
                                  onClick={(value) =>
                                    insertMustacheIntoStepTplVar(idx, value)
                                  }
                                />
                              </Box>
                            </Box>
                          )}
                          <TemplatePreview form={previewForm} compact />
                        </Box>
                      );
                    })()}
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    style={{ marginTop: 6 }}
                  >
                    Janela aberta = texto do passo; fechada = este template.
                  </Typography>
                </Box>
              )}
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={() => removeStep(idx)} startIcon={<Delete />} color="secondary" variant="outlined">Remover Passo</Button>
              </Box>
            </Grid>
          </Grid>
          </Paper>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderPreview = () => {
    if (!preview) return null;
    return (
      <Card style={{ marginTop: 16 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>Preview da Mensagem</Typography>
          <Paper variant="outlined" style={{ padding: 16, whiteSpace: 'pre-wrap', backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f5f5f5', color: theme.palette.text.primary, fontFamily: 'monospace', fontSize: '14px' }}>{preview}</Paper>
          <Box display="flex" alignItems="center" marginTop={1}>
            <Chip icon={<AttachFile />} label="Anexo" size="small" color="secondary" />
            <Typography variant="caption" style={{ marginLeft: 8, color: theme.palette.text.secondary }}>Ícone de anexo aparecerá aqui</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" style={{ gap: 12 }}>
        <Typography variant="h5" style={{ color: theme.palette.text.primary }}>Follow UP - Templates de Fluxo</Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }} flexWrap="wrap">
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Tooltip title="Lista">
              <IconButton
                size="small"
                onClick={() => handleChangeViewMode("list")}
                style={{
                  borderRadius: 8,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor:
                    viewMode === "list" ? theme.palette.primary.main : "transparent",
                  color: viewMode === "list" ? "#fff" : theme.palette.text.secondary,
                }}
                aria-label="Visualização em lista"
              >
                <ViewList style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Grade">
              <IconButton
                size="small"
                onClick={() => handleChangeViewMode("cards")}
                style={{
                  borderRadius: 8,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor:
                    viewMode === "cards" ? theme.palette.primary.main : "transparent",
                  color: viewMode === "cards" ? "#fff" : theme.palette.text.secondary,
                }}
                aria-label="Visualização em grade"
              >
                <ViewModule style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            size="small"
            placeholder="Buscar por nome ou descrição..."
            type="search"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            style={{ minWidth: 220 }}
          />
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => { resetForm(); setOpen(true); }}>Novo Template</Button>
        </Box>
      </Box>
      <Paper variant="outlined" style={{ padding: viewMode === "cards" ? 16 : 0 }}>
        {filteredFloups.length === 0 ? (
          <Box p={3} textAlign="center">
            <Typography color="textSecondary">
              {searchParam.trim()
                ? "Nenhum Follow UP encontrado"
                : "Nenhum Follow UP criado ainda"}
            </Typography>
          </Box>
        ) : viewMode === "cards" ? (
          <Grid container spacing={2}>
            {filteredFloups.map((f) => (
              <Grid item xs={12} sm={6} md={4} key={f.id}>
                <Card variant="outlined" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <CardContent style={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
                      {f.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{
                        marginBottom: 12,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "3.6em",
                      }}
                    >
                      {f.description || "Sem descrição"}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                      <Chip label={f.templateType || "personalizado"} size="small" color="primary" variant="outlined" />
                      <Chip
                        label={`${f.stepsCount || (f.steps || []).length || 0} passos`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={f.isActive ? "Ativo" : "Inativo"}
                        size="small"
                        color={f.isActive ? "primary" : "default"}
                      />
                    </Box>
                  </CardContent>
                  <Box display="flex" justifyContent="flex-end" px={1} pb={1}>
                    <Tooltip title="Editar">
                      <IconButton
                        onClick={async () => {
                          try {
                            const { data } = await api.get(`/plugins/floup/${f.id}`);
                            setStepOficialVars({});
                            setForm({
                              id: data.id,
                              name: data.name || "",
                              description: data.description || "",
                              templateType: data.templateType || "personalizado",
                              condition: data.condition || "queue",
                              conditionValue: data.conditionValue || "",
                              steps: data.steps || [],
                              stopConditions: data.stopConditions || [],
                              pauseConditions: data.pauseConditions || [],
                              whatsappId: data.whatsappId || null,
                              dispatchDays: parseDispatchDays(data.dispatchDays),
                              dispatchStartTime: data.dispatchStartTime || "09:00",
                              dispatchEndTime: data.dispatchEndTime || "18:00",
                              pauseOutsideHours: !!data.pauseOutsideHours,
                            });
                            setOpen(true);
                          } catch (error) {
                            console.error("[FLOUP] Erro ao carregar template para edição:", error);
                            alert("Erro ao carregar o template. Tente novamente.");
                          }
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicar">
                      <IconButton onClick={() => handleDuplicate(f.id)}>
                        <FileCopy />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton onClick={() => handleDelete(f.id)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
        <List>
          {filteredFloups.map(f => (
            <React.Fragment key={f.id}>
              <ListItem>
                <ListItemText primary={f.name} secondary={<div><Typography variant="body2" color="textSecondary">{f.description}</Typography><Box display="flex" gap={1} marginTop={1}><Chip label={f.templateType || "personalizado"} size="small" color="primary" variant="outlined" /><Chip label={`${f.stepsCount || (f.steps || []).length || 0} passos`} size="small" color="default" variant="outlined" /><Chip label={f.isActive ? "Ativo" : "Inativo"} size="small" color={f.isActive ? "primary" : "default"} /></Box></div>} />
                <ListItemSecondaryAction>
                  <Tooltip title="Editar"><IconButton onClick={async () => { 
                    try {
                      const { data } = await api.get(`/plugins/floup/${f.id}`);
                      setStepOficialVars({});
                      setForm({ 
                        id: data.id, 
                        name: data.name || "", 
                        description: data.description || "", 
                        templateType: data.templateType || "personalizado", 
                        condition: data.condition || "queue",
                        conditionValue: data.conditionValue || "",
                        steps: data.steps || [], 
                        stopConditions: data.stopConditions || [], 
                        pauseConditions: data.pauseConditions || [],
                        whatsappId: data.whatsappId || null,
                        dispatchDays: parseDispatchDays(data.dispatchDays),
                        dispatchStartTime: data.dispatchStartTime || "09:00",
                        dispatchEndTime: data.dispatchEndTime || "18:00",
                        pauseOutsideHours: !!data.pauseOutsideHours,
                      }); 
                      setOpen(true);
                    } catch (error) {
                      console.error('[FLOUP] Erro ao carregar template para edição:', error);
                      alert('Erro ao carregar o template. Tente novamente.');
                    }
                  }}><Edit /></IconButton></Tooltip>
                  <Tooltip title="Duplicar"><IconButton onClick={() => handleDuplicate(f.id)}><FileCopy /></IconButton></Tooltip>
                  <Tooltip title="Deletar"><IconButton onClick={() => handleDelete(f.id)}><Delete /></IconButton></Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>
        )}
      </Paper>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center"><Avatar style={{ marginRight: 16 }}><Schedule /></Avatar>{form.id ? "Editar Template" : "Novo Template"}</Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}><Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>Informações Básicas</Typography></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Nome do Template" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel 
                  shrink 
                  style={{ backgroundColor: theme.palette.background.paper, padding: '0 4px' }}
                >
                  Tipo do Template
                </InputLabel>
                <Select 
                  value={form.templateType} 
                  onChange={e => { const config = getTemplateTypeConfig(e.target.value); setForm({ ...form, templateType: e.target.value, steps: config.defaultSteps }); }}
                  notched
                >
                  <MenuItem value="cliente">Cliente</MenuItem>
                  <MenuItem value="lead">Lead</MenuItem>
                  <MenuItem value="personalizado">Personalizado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel
                  shrink
                  style={{ backgroundColor: theme.palette.background.paper, padding: "0 4px" }}
                >
                  Conexão do Envio
                </InputLabel>
                <Select
                  value={form.whatsappId || ""}
                  onChange={(e) => {
                    const value = e.target.value === "" ? null : e.target.value;
                    const wa = whatsapps.find((w) => w.id === value);
                    const leavingOfficial =
                      wa?.channel !== "whatsapp_oficial";
                    setForm((prev) => ({
                      ...prev,
                      whatsappId: value,
                      ...(leavingOfficial
                        ? {
                            steps: (prev.steps || []).map(clearStepOficialTemplate),
                          }
                        : {}),
                    }));
                    if (leavingOfficial) {
                      setStepOficialVars({});
                      setStepTemplateModalIdx(null);
                      setOfficialTemplates([]);
                    }
                  }}
                  displayEmpty
                  notched
                >
                  <MenuItem value="">
                    <em>Padrão do Ticket (mesma conexão atual)</em>
                  </MenuItem>
                  {whatsapps.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name}
                      {w.channel === "whatsapp_oficial"
                        ? " — API Oficial"
                        : w.channel === "telegram"
                          ? " — Telegram"
                          : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                Se deixar em Padrão do Ticket, o disparo sai pela conexão em que o ticket já está.
              </Typography>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Descrição" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} multiline rows={2} /></Grid>
            <Grid item xs={12}>
              <Divider style={{ marginTop: 8, marginBottom: 16 }} />
              <Typography variant="subtitle2" gutterBottom style={{ color: theme.palette.text.primary, marginBottom: 8 }}>Condição para Acionamento</Typography>
              <Typography variant="body2" style={{ color: theme.palette.text.secondary, marginBottom: 16 }}>
                Defina quando este Follow UP deve ser ativado. Uma vez ativado, todos os passos serão executados sequencialmente, até que as condições de parada sejam atendidas.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel 
                  shrink 
                  style={{ color: theme.palette.text.secondary, backgroundColor: theme.palette.background.paper, padding: '0 4px' }}
                >
                  Condição
                </InputLabel>
                <Select 
                  value={form.condition || "queue"} 
                  onChange={e => setForm({ ...form, condition: e.target.value, conditionValue: "" })} 
                  style={{ color: theme.palette.text.primary }}
                  notched
                >
                  <MenuItem value="queue">Fila do sistema</MenuItem>
                  <MenuItem value="tag">Tag do sistema</MenuItem>
                  <MenuItem value="firstClientInteraction">Primeira interação do cliente</MenuItem>
                  <MenuItem value="firstAttendantInteraction">Primeira interação do atendente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {form.condition === "queue" && (
                <FormControl fullWidth variant="outlined">
                  <InputLabel 
                    shrink 
                    style={{ color: theme.palette.text.secondary, backgroundColor: theme.palette.background.paper, padding: '0 4px' }}
                  >
                    Selecionar fila
                  </InputLabel>
                  <Select 
                    value={form.conditionValue || ""} 
                    onChange={e => setForm({ ...form, conditionValue: e.target.value })} 
                    displayEmpty
                    style={{ color: theme.palette.text.primary }}
                    notched
                  >
                    <MenuItem value=""><em>Selecionar fila</em></MenuItem>
                    {queues.map(q => (<MenuItem key={q.id} value={q.name}>{q.name}</MenuItem>))}
                  </Select>
                </FormControl>
              )}
              {form.condition === "tag" && (
                <FormControl fullWidth variant="outlined">
                  <InputLabel 
                    shrink 
                    style={{ color: theme.palette.text.secondary, backgroundColor: theme.palette.background.paper, padding: '0 4px' }}
                  >
                    Selecionar tag
                  </InputLabel>
                  <Select 
                    value={form.conditionValue || ""} 
                    onChange={e => setForm({ ...form, conditionValue: e.target.value })} 
                    displayEmpty
                    style={{ color: theme.palette.text.primary }}
                    notched
                  >
                    <MenuItem value=""><em>Selecionar tag</em></MenuItem>
                    {tags.map(t => (<MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>))}
                  </Select>
                </FormControl>
              )}
              {(form.condition === "firstClientInteraction" || form.condition === "firstAttendantInteraction") && (
                <TextField 
                  fullWidth 
                  label="Descrição da condição" 
                  value={form.conditionValue || ""} 
                  onChange={e => setForm({ ...form, conditionValue: e.target.value })} 
                  placeholder="Ex: Cliente enviou primeira mensagem"
                  InputLabelProps={{
                    style: { color: theme.palette.text.secondary }
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" style={{ 
                padding: 16, 
                backgroundColor: theme.palette.type === 'dark' ? 'rgba(33, 150, 243, 0.15)' : '#e3f2fd', 
                border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(33, 150, 243, 0.3)' : '#2196f3'}` 
              }}>
                <Typography variant="body2" style={{ color: theme.palette.type === 'dark' ? theme.palette.text.primary : '#1976d2' }}>
                  <strong>{getTemplateTypeConfig(form.templateType).title}</strong><br/>
                  {getTemplateTypeConfig(form.templateType).description}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Divider style={{ marginTop: 16, marginBottom: 16 }} />
              <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
                Horário comercial do disparo
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                Igual à Campanha PRO: se o Follow UP cair fora do horário, espera o próximo dia/horário útil.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.pauseOutsideHours}
                    onChange={(e) =>
                      setForm({ ...form, pauseOutsideHours: e.target.checked })
                    }
                    color="primary"
                  />
                }
                label="Enviar apenas em horário comercial (reagendar para o próximo dia útil)"
              />
            </Grid>
            {form.pauseOutsideHours && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                    Dias da semana para disparo:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                    {[
                      { value: "monday", label: "Segunda" },
                      { value: "tuesday", label: "Terça" },
                      { value: "wednesday", label: "Quarta" },
                      { value: "thursday", label: "Quinta" },
                      { value: "friday", label: "Sexta" },
                      { value: "saturday", label: "Sábado" },
                      { value: "sunday", label: "Domingo" },
                    ].map((day) => {
                      const isSelected =
                        Array.isArray(form.dispatchDays) &&
                        form.dispatchDays.includes(day.value);
                      return (
                        <Chip
                          key={day.value}
                          label={day.label}
                          onClick={() => {
                            const current = Array.isArray(form.dispatchDays)
                              ? [...form.dispatchDays]
                              : [];
                            if (isSelected) {
                              const idx = current.indexOf(day.value);
                              if (idx > -1) current.splice(idx, 1);
                            } else {
                              current.push(day.value);
                            }
                            setForm({ ...form, dispatchDays: current });
                          }}
                          color={isSelected ? "primary" : "default"}
                          variant={isSelected ? "default" : "outlined"}
                          style={{ cursor: "pointer" }}
                        />
                      );
                    })}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Horário de início"
                    type="time"
                    value={form.dispatchStartTime || "09:00"}
                    onChange={(e) =>
                      setForm({ ...form, dispatchStartTime: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Horário de fim"
                    type="time"
                    value={form.dispatchEndTime || "18:00"}
                    onChange={(e) =>
                      setForm({ ...form, dispatchEndTime: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Divider style={{ marginTop: 16, marginBottom: 16 }} />
              <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>Controles de Parada do Fluxo</Typography>
              <Paper variant="outlined" style={{ 
                padding: 16, 
                backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 152, 0, 0.15)' : '#fff3e0', 
                border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255, 152, 0, 0.3)' : '#ff9800'}` 
              }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel 
                      control={
                        <Switch 
                          checked={form.stopConditions?.some(c => c.type === 'anyMessage') || false} 
                          onChange={e => {
                            const stopConditions = form.stopConditions || [];
                            if (e.target.checked) {
                              if (!stopConditions.some(c => c.type === 'anyMessage')) {
                                setForm({ ...form, stopConditions: [...stopConditions, { type: 'anyMessage', enabled: true }] });
                              }
                            } else {
                              setForm({ ...form, stopConditions: stopConditions.filter(c => c.type !== 'anyMessage') });
                            }
                          }} 
                        />
                      } 
                      label="1. Caso o Contato responder qualquer mensagem"
                      style={{ color: theme.palette.text.primary }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel 
                      control={
                        <Switch 
                          checked={form.stopConditions?.some(c => c.type === 'keyword') || false} 
                          onChange={e => {
                            const stopConditions = form.stopConditions || [];
                            if (e.target.checked) {
                              if (!stopConditions.some(c => c.type === 'keyword')) {
                                setForm({ ...form, stopConditions: [...stopConditions, { type: 'keyword', enabled: true, keyword: '' }] });
                              }
                            } else {
                              setForm({ ...form, stopConditions: stopConditions.filter(c => c.type !== 'keyword') });
                            }
                          }} 
                        />
                      } 
                      label="2. Caso o Contato responder uma Palavra chave"
                      style={{ color: theme.palette.text.primary }}
                    />
                    {form.stopConditions?.some(c => c.type === 'keyword') && (
                      <TextField 
                        fullWidth 
                        label="Palavra chave" 
                        value={form.stopConditions.find(c => c.type === 'keyword')?.keyword || ''} 
                        onChange={e => {
                          const stopConditions = form.stopConditions || [];
                          const keywordCondition = stopConditions.find(c => c.type === 'keyword');
                          if (keywordCondition) {
                            keywordCondition.keyword = e.target.value;
                            setForm({ ...form, stopConditions: [...stopConditions.filter(c => c.type !== 'keyword'), keywordCondition] });
                          }
                        }} 
                        placeholder="Ex: PARAR, CANCELAR, SAIR" 
                        style={{ marginTop: 8 }}
                        InputLabelProps={{
                          style: { color: theme.palette.text.secondary }
                        }}
                        InputProps={{
                          style: { color: theme.palette.text.primary }
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel 
                      control={
                        <Switch 
                          checked={form.stopConditions?.some(c => c.type === 'ticketClosed') || false} 
                          onChange={e => {
                            const stopConditions = form.stopConditions || [];
                            if (e.target.checked) {
                              if (!stopConditions.some(c => c.type === 'ticketClosed')) {
                                setForm({ ...form, stopConditions: [...stopConditions, { type: 'ticketClosed', enabled: true }] });
                              }
                            } else {
                              setForm({ ...form, stopConditions: stopConditions.filter(c => c.type !== 'ticketClosed') });
                            }
                          }} 
                        />
                      } 
                      label="3. Caso o Ticket for Fechado"
                      style={{ color: theme.palette.text.primary }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={2}>
                <Typography variant="h6" style={{ color: theme.palette.text.primary }}>Passos do Fluxo</Typography>
                <Button onClick={addStep} startIcon={<Add />} variant="outlined">Adicionar Passo</Button>
              </Box>
              <Typography
                variant="body2"
                style={{
                  marginTop: 8,
                  marginBottom: 4,
                  color: theme.palette.text.secondary,
                  lineHeight: 1.45,
                }}
              >
                O tempo de cada passo é sequencial: o Passo 1 conta a partir da ativação do Follow-up;
                os passos seguintes contam a partir do envio do passo anterior (ex.: 20h + 23h = 43h no total).
              </Typography>
            </Grid>
            {form.steps.map((step, idx) => renderStepForm(step, idx))}
            {renderPreview()}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} startIcon={<Close />}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" color="primary" startIcon={<Save />} disabled={!form.name || form.steps.length === 0}>Salvar Template</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={stepTemplateModalIdx !== null}
        onClose={closeStepTemplateModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Template Meta (janela fechada)
          {stepTemplateModalIdx != null
            ? ` — Passo ${(form.steps[stepTemplateModalIdx]?.order) || stepTemplateModalIdx + 1}`
            : ""}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Quando o ticket estiver em API Oficial e a janela de 24h estiver fechada, o Follow UP
            enviará este template em vez do texto livre do passo.
          </Typography>
          <FormControl fullWidth variant="outlined">
            <InputLabel shrink>Template aprovado</InputLabel>
            <Select
              value={
                stepTemplateModalIdx != null &&
                form.steps[stepTemplateModalIdx]?.oficialTemplateName &&
                form.steps[stepTemplateModalIdx]?.oficialTemplateLanguage
                  ? `${form.steps[stepTemplateModalIdx].oficialTemplateName}|${form.steps[stepTemplateModalIdx].oficialTemplateLanguage}`
                  : ""
              }
              onChange={(e) => {
                const stepIdx = stepTemplateModalIdx;
                if (stepIdx == null) return;
                const v = e.target.value;
                if (!v) {
                  setForm((prev) => {
                    const steps = [...prev.steps];
                    if (!steps[stepIdx]) return prev;
                    steps[stepIdx] = clearStepOficialTemplate(steps[stepIdx]);
                    return { ...prev, steps };
                  });
                  setStepOficialVars((prev) => {
                    const next = { ...prev };
                    delete next[stepIdx];
                    return next;
                  });
                  return;
                }
                const u = v.indexOf("|");
                const n = u < 0 ? v : v.slice(0, u);
                const lang = u < 0 ? "" : v.slice(u + 1);
                const tpl = officialTemplates.find(
                  (t) =>
                    t.name === n && String(t.language || "") === String(lang || "")
                );
                const sendJson = syncStepOficialVars(stepIdx, tpl, null);
                setForm((prev) => {
                  const steps = [...prev.steps];
                  if (!steps[stepIdx]) return prev;
                  steps[stepIdx] = {
                    ...steps[stepIdx],
                    oficialTemplateName: n,
                    oficialTemplateLanguage: lang,
                    oficialTemplateComponentsJson: sendJson,
                  };
                  return { ...prev, steps };
                });
              }}
              displayEmpty
              notched
              label="Template aprovado"
              disabled={!form.whatsappId || loadingOfficialTemplates}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 420, width: 520 },
                },
              }}
              renderValue={(selected) => {
                if (!selected) return "";
                const u = String(selected).indexOf("|");
                const n = u < 0 ? selected : String(selected).slice(0, u);
                const lang = u < 0 ? "" : String(selected).slice(u + 1);
                const tpl = officialTemplates.find(
                  (t) =>
                    t.name === n && String(t.language || "") === String(lang || "")
                );
                const meta = categoryMetaWithIcon(tpl?.category);
                const Icon = meta.Icon;
                return (
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Icon style={{ fontSize: 18, color: meta.color }} />
                    <span>
                      {n} — {lang || "—"}
                    </span>
                  </Box>
                );
              }}
            >
              {loadingOfficialTemplates && (
                <MenuItem value="">
                  <CircularProgress size={16} style={{ marginRight: 8 }} />
                  Carregando…
                </MenuItem>
              )}
              {!loadingOfficialTemplates && officialTemplates.length === 0 && (
                <MenuItem value="">
                  <em>Nenhum template aprovado</em>
                </MenuItem>
              )}
              {officialTemplates.map((t) => {
                const keyv = `${t.name}|${t.language || ""}`;
                const meta = categoryMetaWithIcon(t.category);
                const Icon = meta.Icon;
                return (
                  <MenuItem key={keyv} value={keyv}>
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ gap: 8, width: "100%" }}
                    >
                      <Icon
                        style={{
                          fontSize: 18,
                          color: meta.color,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ flex: 1 }}>
                        {t.name} — {t.language || "—"}
                      </span>
                      <Chip
                        size="small"
                        label={meta.label}
                        style={{
                          backgroundColor: meta.backgroundColor,
                          color: meta.color,
                        }}
                      />
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const stepIdx = stepTemplateModalIdx;
              if (stepIdx == null) return;
              setForm((prev) => {
                const steps = [...prev.steps];
                if (!steps[stepIdx]) return prev;
                steps[stepIdx] = clearStepOficialTemplate(steps[stepIdx]);
                return { ...prev, steps };
              });
              setStepOficialVars((prev) => {
                const next = { ...prev };
                delete next[stepIdx];
                return next;
              });
            }}
          >
            Limpar
          </Button>
          <Button
            onClick={closeStepTemplateModal}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteConfirmOpen} onClose={() => { setDeleteConfirmOpen(false); setFloupToDelete(null); }}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja deletar o Follow UP <strong>"{floupToDelete?.name}"</strong>?
            <br />
            <br />
            Esta ação não pode ser desfeita e todas as mídias associadas serão excluídas permanentemente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirmOpen(false); setFloupToDelete(null); }} color="primary">
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="secondary" variant="contained" startIcon={<Delete />}>
            Deletar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FloupPage;


